import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { FiArrowRight, FiCalendar } from 'react-icons/fi';
import Header from '../components/Header';
import Footer from '../components/layout/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import { blogService, buildImageUrl } from '../services/api';
import { Container, Eyebrow, Section, SectionSubtitle, SectionTitle, Spinner } from '../components/ui';
import Seo, { organizationSchema } from '../components/Seo';
import { useSettings } from '../contexts/SettingsContext';

interface BlogPostSummary {
  slug: string;
  title: string;
  excerpt?: string;
  featured_image?: string;
  featured_image_alt?: string;
  published_at: string;
}

const Page = styled.div`
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.background};
`;

const Head = styled.div`
  max-width: 720px;
  margin-bottom: 2.5rem;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1.5rem;
  @media (max-width: 900px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

const Card = styled(Link)`
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.card};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.card};
  color: ${({ theme }) => theme.colors.foreground};
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  &:hover { transform: translateY(-4px); box-shadow: ${({ theme }) => theme.shadows.hover}; }
`;

const Cover = styled.div`
  aspect-ratio: 16 / 9;
  background: ${({ theme }) => theme.colors.surface};
  img { width: 100%; height: 100%; object-fit: cover; }
`;

const Body = styled.div`
  padding: 1.3rem;
  display: flex;
  flex: 1;
  flex-direction: column;
  .date { display: flex; align-items: center; gap: 0.4rem; color: ${({ theme }) => theme.colors.text.secondary}; font-size: 0.82rem; }
  h2 { margin: 0.7rem 0; font-size: 1.15rem; line-height: 1.35; }
  p { color: ${({ theme }) => theme.colors.text.secondary}; line-height: 1.6; font-size: 0.92rem; }
  .more { margin-top: auto; padding-top: 1rem; display: flex; align-items: center; gap: 0.4rem; color: ${({ theme }) => theme.colors.primary}; font-weight: 600; }
`;

const State = styled.div`
  min-height: 240px;
  display: grid;
  place-items: center;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const formatDate = (value: string) =>
  new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' }).format(new Date(value));

const BlogPage: React.FC = () => {
  const { siteSettings } = useSettings();
  const [posts, setPosts] = useState<BlogPostSummary[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    blogService.getPosts({ limit: 100 })
      .then((response) => setPosts(response.items || []))
      .finally(() => setLoading(false));
  }, []);

  return (
    <Page>
      <Seo
        title={`Blog imobiliário | ${siteSettings?.company_info?.name || siteSettings?.site_name || 'Imobiliária'}`}
        description="Informação para comprar, vender, alugar e investir melhor no mercado imobiliário."
        canonicalPath="/blog"
        structuredData={organizationSchema(siteSettings)}
      />
      <Header />
      <WhatsAppButton />
      <Section>
        <Container>
          <Head>
            <Eyebrow>Novidades</Eyebrow>
            <SectionTitle as="h1">Blog iLaura</SectionTitle>
            <SectionSubtitle>Informação para comprar, vender, alugar e investir melhor.</SectionSubtitle>
          </Head>
          {loading ? <State><Spinner $size={34} /></State> : (
            <Grid>
              {posts.map((post) => (
                <Card key={post.slug} to={`/blog/${post.slug}`}>
                  <Cover>
                    {post.featured_image && <img src={buildImageUrl(post.featured_image)} alt={post.featured_image_alt || post.title} />}
                  </Cover>
                  <Body>
                    <span className="date"><FiCalendar /> {formatDate(post.published_at)}</span>
                    <h2>{post.title}</h2>
                    {post.excerpt && <p>{post.excerpt}</p>}
                    <span className="more">Leia mais <FiArrowRight /></span>
                  </Body>
                </Card>
              ))}
            </Grid>
          )}
        </Container>
      </Section>
      <Footer />
    </Page>
  );
};

export default BlogPage;
